import _ from 'lodash';
import { placeableBoardsOf, placeableColumnsOf, type PlaceableBoard } from './moveChangePlacement';

const TITLE_LANGS = ['fr', 'en', 'de'] as const;

const STATUS_FINISHED = 'finished';
const STATUS_DEPLOYED = 'deployed';
const STATUS_PENDING_DEPLOYMENT = 'pendingDeployment';
const ROLE_CLIENT = 'client';

export type LinkedChangeSource = {
    _id: string;
    slug?: number;
    type?: string;
    typeDomain?: string;
    typeSubtype?: string;
    project?: string | { _id?: string };
    client?: string | { _id?: string };
    importance?: number;
    billable?: boolean;
    billingMethod?: string;
    estimateAsFloor?: boolean;
    estimable?: boolean;
    completionNotify?: {
        emails?: string[];
        signalUsers?: string[];
        signalGroups?: string[];
    };
    subscribers?: string[];
    replyRecipients?: AnyObj;
    title?: AnyObj;
    description?: AnyObj;
    progress?: { status?: string };
    localizedTitle?: (lang: string) => string | null;
    slugName?: () => string;
};

export type LinkedChangePlacement = {
    boardId?: string;
    columnId?: string;
};

/**
 * Finished / deployed cards that can spawn a follow-up linked card
 * (excludes merged).
 */
export function canSpawnLinkedChange(change?: LinkedChangeSource | null): boolean {
    if (!change?.progress?.status)
        return false;
    return [
        STATUS_FINISHED,
        STATUS_DEPLOYED,
        STATUS_PENDING_DEPLOYMENT,
    ].includes(change.progress.status);
}

/** Client / intake message that can seed a linked-card description. */
export function isClientInboundMessage(comment?: AnyObj | null): boolean {
    if (!comment)
        return false;
    if (typeof comment.isReply === 'function' ? comment.isReply() : comment.kind === 'reply')
        return false;
    if (typeof comment.isEmailIntake === 'function' ? comment.isEmailIntake() : comment.source === 'email_intake')
        return true;
    if (typeof comment.isRtImport === 'function' ? comment.isRtImport() : comment.source === 'rt')
        return true;
    return comment.author?.role === ROLE_CLIENT;
}

function escapeHtmlText(text: string): string {
    return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function localizedTitleOf(source: LinkedChangeSource, lang: string): string {
    if (typeof source.localizedTitle === 'function')
        return source.localizedTitle(lang) || '';
    if (!source.title)
        return '';
    if (lang in source.title && source.title[lang])
        return String(source.title[lang]);
    const first = Object.keys(source.title)[0];
    return first ? String(source.title[first]) : '';
}

function slugNameOf(source: LinkedChangeSource): string {
    if (typeof source.slugName === 'function')
        return source.slugName();
    return source.slug != null ? String(source.slug) : '';
}

function prefixedLocalizedTitle(source: LinkedChangeSource, prefix: string): AnyObj {
    const title: AnyObj = {};
    TITLE_LANGS.forEach((lang) => {
        const value = localizedTitleOf(source, lang);
        if (value)
            title[lang] = `${prefix}${value}`;
    });
    if (!Object.keys(title).length) {
        const fallback = localizedTitleOf(source, 'fr') || slugNameOf(source) || '';
        title.fr = `${prefix}${fallback}`;
    }
    return title;
}

function emptyReplyRecipients() {
    return {
        to: { emails: [], signalUsers: [], signalGroups: [] },
        cc: { emails: [], signalUsers: [], signalGroups: [] },
    };
}

/**
 * Build create payload: copy metadata from source, reset lifecycle fields.
 * Client seed text is posted as a timeline comment after create (not description).
 */
export function buildLinkedChangePayload(
    source: LinkedChangeSource,
    options: { titlePrefix: string } = { titlePrefix: 'Suite : ' },
): AnyObj {
    const projectId = _.isPlainObject(source.project)
        ? (source.project as AnyObj)._id
        : source.project;
    const clientId = source.client
        ? (_.isPlainObject(source.client) ? (source.client as AnyObj)._id : source.client)
        : undefined;

    const payload: AnyObj = {
        project: projectId,
        type: source.type,
        typeDomain: source.typeDomain,
        typeSubtype: source.typeSubtype,
        importance: source.importance,
        billable: source.billable,
        billingMethod: source.billingMethod,
        estimateAsFloor: source.estimateAsFloor,
        estimable: source.estimable,
        completionNotify: {
            emails: [...(source.completionNotify?.emails || [])],
            signalUsers: [...(source.completionNotify?.signalUsers || [])],
            signalGroups: [...(source.completionNotify?.signalGroups || [])],
        },
        subscribers: [...(source.subscribers || [])],
        replyRecipients: _.cloneDeep(source.replyRecipients || emptyReplyRecipients()),
        title: prefixedLocalizedTitle(source, options.titlePrefix),
        description: {},
    };

    if (clientId)
        payload.client = clientId;

    return payload;
}

/**
 * Prefer the loaded board when it belongs to the same project (finished cards
 * are often unboarded). Else first placeable board/column on the project.
 */
export function resolveLinkedChangePlacement(
    source: LinkedChangeSource,
    board?: PlaceableBoard | null,
    project?: { _id?: string; boards?: PlaceableBoard[] } | null,
): LinkedChangePlacement {
    const projectId = _.isPlainObject(source.project)
        ? (source.project as AnyObj)._id
        : source.project;
    const boardProjectRaw = board ? (board as AnyObj).project : undefined;
    const boardProjectId = _.isPlainObject(boardProjectRaw)
        ? (boardProjectRaw as AnyObj)._id
        : boardProjectRaw;

    if (board?._id && (!boardProjectId || boardProjectId === projectId)) {
        const onBoard = (board.columns || []).find((column) =>
            !column.isSmart &&
            (column.changes || []).some((c: AnyObj | string) =>
                (_.isPlainObject(c) ? (c as AnyObj)._id : c) === source._id
            )
        );
        if (onBoard?._id)
            return { boardId: board._id, columnId: onBoard._id };

        const fallbackColumn = placeableColumnsOf(board)[0];
        if (fallbackColumn?._id)
            return { boardId: board._id, columnId: fallbackColumn._id };
    }

    const boards = placeableBoardsOf(project);
    const firstBoard = boards[0];
    const firstColumn = placeableColumnsOf(firstBoard)[0];
    if (firstBoard?._id && firstColumn?._id)
        return { boardId: firstBoard._id, columnId: firstColumn._id };

    return {};
}

function changeSlugAnchorHtml(change: LinkedChangeSource, label: string): string {
    const number = String(change.slug ?? '');
    return (
        `<a href="/changes/by-slug/${escapeHtmlText(number)}"`
        + ` data-yoda-change-slug="${escapeHtmlText(number)}"`
        + ` class="yoda-change-slug">${label}</a>`
    );
}

export function linkedChangeCreatedCommentHtml(
    created: LinkedChangeSource,
    prefixLabel: string,
): string {
    const title = escapeHtmlText(localizedTitleOf(created, 'fr') || localizedTitleOf(created, 'en'));
    const slug = escapeHtmlText(slugNameOf(created));
    const label = `${slug}${title ? ` — ${title}` : ''}`;
    return `<p>${escapeHtmlText(prefixLabel)} ${changeSlugAnchorHtml(created, label)}</p>`;
}

export function linkedChangeSourceCommentHtml(
    source: LinkedChangeSource,
    prefixLabel: string,
): string {
    const title = escapeHtmlText(localizedTitleOf(source, 'fr') || localizedTitleOf(source, 'en'));
    const slug = escapeHtmlText(slugNameOf(source));
    const label = `${slug}${title ? ` — ${title}` : ''}`;
    return `<p>${escapeHtmlText(prefixLabel)} ${changeSlugAnchorHtml(source, label)}</p>`;
}
